import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  Input,
  Textarea,
  VStack,
  HStack,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Divider,
  IconButton,
  useToast,
  Card,
  CardBody,
  CardHeader,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Image
} from '@chakra-ui/react';
import { DownloadIcon, AttachmentIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';
import HeaderMenus from '../HeaderMenus';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const [project, setProject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [newMessage, setNewMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const fetchProject = async () => {
    try {
      const response = await callApi(`/api/projects/${id}`, accessToken);
      if (!response.ok) throw new Error('Failed to fetch project');
      const data = await response.json();
      setProject(data);
    } catch (err) {
      setError(err.message || 'Failed to load project');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (accessToken && id) {
      fetchProject();
      
      // Auto-refresh every 10 seconds
      const intervalId = setInterval(() => {
        fetchProject();
      }, 10000);
      
      return () => clearInterval(intervalId);
    }
  }, [accessToken, id]);

  useEffect(() => {
    // Only auto-scroll if we are already near the bottom (within 150px)
    // or if this is the initial load
    if (messagesEndRef.current) {
      const container = messagesEndRef.current.parentElement;
      if (container) {
        const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 150;
        if (isNearBottom || !project?.doc?.messages?.length) {
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [project?.doc?.messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setIsSending(true);
    try {
      const response = await callApi(`/api/projects/${id}/messages`, accessToken, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: newMessage })
      });
      if (!response.ok) throw new Error('Failed to send message');
      
      setNewMessage('');
      await fetchProject(); // Refresh to get the new message
      
      // Force scroll to bottom when the user sends a message
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch (err) {
      toast({
        title: 'Failed to send message',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await callApi(`/api/projects/${id}/files/upload`, accessToken, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload file');
      }

      toast({
        title: 'File uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      await fetchProject(); // Refresh to show new file
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = ''; // Reset input
      }
    }
  };

  const handleDownload = async (fileId, filename) => {
    try {
      const response = await callApi(`/api/projects/${id}/files/${fileId}/download-url`, accessToken);
      if (!response.ok) throw new Error('Failed to get download URL');
      const data = await response.json();
      
      // Create a temporary link to trigger download
      const link = document.createElement('a');
      link.href = data.download_url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      toast({
        title: 'Download failed',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleViewFile = async (file) => {
    setSelectedFile(file);
    setFileContent(null);
    onOpen();
    
    try {
      setIsFileLoading(true);
      // Pass inline=true so the backend sets ResponseContentDisposition=inline
      const response = await callApi(`/api/projects/${id}/files/${file.id}/download-url?inline=true`, accessToken);
      if (!response.ok) throw new Error('Failed to get file URL');
      const data = await response.json();
      
      if (file.filename.toLowerCase().endsWith('.txt')) {
        const textResponse = await fetch(data.download_url);
        const text = await textResponse.text();
        setFileContent(text);
      } else {
        // For PDF, images, and others, we just pass the URL
        setFileContent(data.download_url);
      }
    } catch (err) {
      toast({
        title: 'Failed to load file',
        description: err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      onClose();
    } finally {
      setIsFileLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <HeaderMenus />
        <Flex justify="center" p={10}>
          <Spinner size="xl" />
        </Flex>
      </Box>
    );
  }

  if (error || !project) {
    return (
      <Box>
        <HeaderMenus />
        <Box p={8}>
          <Alert status="error">
            <AlertIcon />
            {error || 'Project not found'}
          </Alert>
          <Button mt={4} leftIcon={<ArrowBackIcon />} onClick={() => navigate('/projects')}>
            Back to Projects
          </Button>
        </Box>
      </Box>
    );
  }

  const doc = project.doc || {};
  const messages = doc.messages || [];
  const files = doc.files || [];

  // Helper function to make URLs clickable
  const renderMessageBody = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = text.split(urlRegex);
    
    return parts.map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <Link key={i} href={part} color="blue.500" isExternal textDecoration="underline">
            {part}
          </Link>
        );
      }
      return part;
    });
  };

  return (
    <Box>
      <HeaderMenus />
      <Box p={8} maxWidth="1400px" margin="0 auto" height="calc(100vh - 100px)">
        <Flex mb={6} alignItems="center">
        <IconButton 
          icon={<ArrowBackIcon />} 
          variant="ghost" 
          mr={4} 
          onClick={() => navigate('/projects')}
          aria-label="Back to projects"
        />
        <Heading size="lg" mr={4}>{doc.name}</Heading>
        <Badge colorScheme={doc.status === 'active' ? 'green' : 'gray'}>
          {doc.status}
        </Badge>
      </Flex>

      <Flex gap={6} height="calc(100% - 80px)">
        {/* Left Column: Messages */}
        <Card flex="2" display="flex" flexDirection="column">
          <CardHeader bg="gray.50" borderBottomWidth="1px" py={3}>
            <Heading size="md">Messages</Heading>
          </CardHeader>
          <CardBody display="flex" flexDirection="column" p={0} overflow="hidden">
            <Box flex="1" overflowY="auto" p={4} bg="gray.50">
              {messages.length === 0 ? (
                <Text color="gray.500" textAlign="center" mt={10}>
                  No messages yet. Start the conversation!
                </Text>
              ) : (
                <VStack spacing={4} align="stretch">
                  {messages.map((msg) => (
                    <Box key={msg.id} bg="white" p={3} borderRadius="md" shadow="sm">
                      <Flex justify="space-between" align="center" mb={2}>
                        <Text fontSize="xs" fontWeight="bold" color="gray.600">
                          {msg.author_name || msg.author_email || 'Unknown User'}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {new Date(msg.created_at.endsWith('Z') ? msg.created_at : msg.created_at + 'Z').toLocaleString(undefined, {
                            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                          })}
                        </Text>
                      </Flex>
                      <Text whiteSpace="pre-wrap">{renderMessageBody(msg.body)}</Text>
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </VStack>
              )}
            </Box>
            <Box p={4} borderTopWidth="1px" bg="white">
              <form onSubmit={handleSendMessage}>
                <HStack align="flex-end">
                  <Textarea 
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    disabled={isSending}
                    minRows={1}
                    maxRows={5}
                    resize="vertical"
                    onKeyDown={(e) => {
                      // Submit on Enter (without Shift)
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        if (newMessage.trim()) {
                          handleSendMessage(e);
                        }
                      }
                    }}
                  />
                  <Button 
                    type="submit" 
                    colorScheme="blue" 
                    isLoading={isSending}
                    isDisabled={!newMessage.trim()}
                    mb={1}
                  >
                    Send
                  </Button>
                </HStack>
              </form>
            </Box>
          </CardBody>
        </Card>

        {/* Right Column: Files */}
        <Card flex="1" display="flex" flexDirection="column">
          <CardHeader bg="gray.50" borderBottomWidth="1px" py={3}>
            <Flex justify="space-between" align="center">
              <Heading size="md">Files</Heading>
              <Button 
                size="sm" 
                leftIcon={<AttachmentIcon />} 
                onClick={() => fileInputRef.current?.click()}
                isLoading={isUploading}
              >
                Upload
              </Button>
              <input 
                type="file" 
                ref={fileInputRef} 
                style={{ display: 'none' }} 
                onChange={handleFileUpload}
              />
            </Flex>
          </CardHeader>
          <CardBody overflowY="auto" p={0}>
            {files.length === 0 ? (
              <Text color="gray.500" textAlign="center" mt={10} p={4}>
                No files uploaded yet.
              </Text>
            ) : (
              <VStack spacing={0} align="stretch" divider={<Divider />}>
                {files.map((file) => (
                  <Flex key={file.id} p={4} justify="space-between" align="center" _hover={{ bg: 'gray.50' }}>
                    <Box overflow="hidden" flex="1" mr={2}>
                      <Text 
                        fontWeight="medium" 
                        isTruncated 
                        title={file.filename}
                        cursor="pointer"
                        _hover={{ color: 'blue.500', textDecoration: 'underline' }}
                        onClick={() => handleViewFile(file)}
                      >
                        {file.filename}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {(file.size_bytes / 1024).toFixed(1)} KB • {new Date(file.created_at.endsWith('Z') ? file.created_at : file.created_at + 'Z').toLocaleDateString()}
                      </Text>
                    </Box>
                    <IconButton 
                      icon={<DownloadIcon />} 
                      size="sm" 
                      variant="ghost"
                      colorScheme="blue"
                      onClick={() => handleDownload(file.id, file.filename)}
                      aria-label="Download file"
                    />
                  </Flex>
                ))}
              </VStack>
            )}
          </CardBody>
        </Card>
      </Flex>

      {/* File Viewer Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent height="90vh">
          <ModalHeader>
            {selectedFile?.filename}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display="flex" flexDirection="column" p={0}>
            {isFileLoading ? (
              <Flex justify="center" align="center" flex="1">
                <Spinner size="xl" />
              </Flex>
            ) : (
              <Box flex="1" w="100%" h="100%" bg="gray.50" p={selectedFile?.filename.toLowerCase().endsWith('.txt') ? 4 : 0}>
                {selectedFile?.filename.toLowerCase().endsWith('.txt') ? (
                  <Box 
                    whiteSpace="pre-wrap" 
                    fontFamily="monospace" 
                    overflowY="auto" 
                    h="100%"
                    p={4}
                    bg="white"
                    borderWidth="1px"
                    borderRadius="md"
                  >
                    {fileContent}
                  </Box>
                ) : selectedFile?.filename.toLowerCase().endsWith('.pdf') ? (
                  <iframe 
                    src={fileContent} 
                    width="100%" 
                    height="100%" 
                    style={{ border: 'none' }}
                    title="PDF Viewer"
                  />
                ) : selectedFile?.filename.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/) ? (
                  <Flex justify="center" align="center" h="100%" bg="gray.100" overflow="auto" p={4}>
                    <Image 
                      src={fileContent} 
                      alt={selectedFile.filename} 
                      maxW="100%" 
                      maxH="100%" 
                      objectFit="contain" 
                      boxShadow="md"
                    />
                  </Flex>
                ) : (
                  <Flex justify="center" align="center" h="100%" direction="column">
                    <Text color="gray.500" mb={4}>Preview not available for this file type.</Text>
                    <Button onClick={() => handleDownload(selectedFile.id, selectedFile.filename)}>
                      Download File
                    </Button>
                  </Flex>
                )}
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" mr={3} onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      </Box>
    </Box>
  );
};

export default ProjectDetail;